import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Skeleton } from '../../components/ui/skeleton';
import useContentProtection from '../../hooks/useContentProtection';
import { 
  BookOpen, 
  Clock, 
  Target,
  CheckCircle2,
  Play,
  Award,
  Mic,
  FileText,
  Keyboard,
  GraduationCap,
  ListChecks,
  Sparkles,
  ChevronRight,
  Info
} from 'lucide-react';

const RPATOverview = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Apply content protection
  useContentProtection(true);
  
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState(null);

  const fetchCourseInfo = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch course details and check enrollment
      const [courseRes, dashboardRes] = await Promise.all([
        api.get('/rpat/courses/rpat-001').catch(() => null),
        api.get('/rpat/dashboard').catch(() => null)
      ]);
      
      if (courseRes?.data) {
        setCourse(courseRes.data);
      }
      
      if (dashboardRes?.data?.enrolled) {
        setEnrolled(true);
      }
    } catch (err) {
      console.error('Failed to fetch course info:', err);
      setError('Failed to load course information');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchCourseInfo();
  }, [fetchCourseInfo]);

  const handleEnroll = async () => {
    try {
      setEnrolling(true);
      await api.post('/rpat/courses/rpat-001/enroll');
      setEnrolled(true);
      // Navigate to dashboard after enrollment
      navigate('/rpat');
    } catch (err) {
      console.error('Enrollment failed:', err);
      setError('Failed to enroll. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 space-y-6">
          <Skeleton className="h-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-48" />
        </div>
      </MainLayout>
    );
  }

  // Course overview content
  const courseOverview = {
    name: "R-PAT: Recruitment Process Autonomous Training",
    tagline: "Transform into a confident, skilled recruiter",
    description: "A comprehensive 53-hour voice-based training program designed to transform fresh hires into skilled recruiters. Master the art of recruitment through interactive lessons, real-world simulations, and AI-powered assessments.",
    totalHours: 53,
    totalTopics: 23,
    totalAssessments: 23,
    typingTests: 10,
    playbooks: "1,200+ pages",
    phases: [
      {
        name: "Foundation",
        topics: 8,
        description: "Understanding the recruitment industry, ecosystem, and career paths",
        duration: "~15 hours"
      },
      {
        name: "Core Skills",
        topics: 7,
        description: "Job descriptions, sourcing, screening, and interview basics",
        duration: "~14 hours"
      },
      {
        name: "Communication & Interview",
        topics: 5,
        description: "Client communication, objection handling, and interview techniques",
        duration: "~12 hours"
      },
      {
        name: "Practical Application",
        topics: 3,
        description: "Real-world scenarios, case studies, and final assessment",
        duration: "~12 hours"
      }
    ],
    features: [
      { icon: Mic, text: "Voice-based lessons for immersive learning" },
      { icon: ListChecks, text: "80% pass rate required on all assessments" },
      { icon: Keyboard, text: "Typing tests to build professional speed" },
      { icon: FileText, text: "1,200+ pages of playbook reference materials" },
      { icon: Award, text: "TekWissen Certified Recruiter credential" },
      { icon: Target, text: "AI-powered feedback and coaching" }
    ],
    certification: {
      name: "TekWissen Certified Recruiter",
      requirements: [
        "Complete all 23 topics",
        "Pass all mock assessments (80%+ required)",
        "Complete 10 typing tests",
        "Pass final certification exam (85%+ required)"
      ]
    }
  };

  return (
    <MainLayout>
      <div className="p-6 space-y-8">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-cyan-600 via-blue-600 to-violet-600 p-8 text-white">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjIiIGZpbGw9IndoaXRlIiBmaWxsLW9wYWNpdHk9IjAuMSIvPjwvZz48L3N2Zz4=')] opacity-50" />
          
          <div className="relative z-10 max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <GraduationCap className="h-8 w-8" />
              <Badge className="bg-white/20 text-white border-white/30">New Hire Training</Badge>
            </div>
            
            <h1 className="text-4xl font-bold mb-2">{courseOverview.name}</h1>
            <p className="text-xl text-white/90 mb-4">{courseOverview.tagline}</p>
            <p className="text-white/80 mb-6 max-w-2xl">{courseOverview.description}</p>
            
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <Clock className="h-5 w-5" />
                <span>{courseOverview.totalHours} hours</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <BookOpen className="h-5 w-5" />
                <span>{courseOverview.totalTopics} topics</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <ListChecks className="h-5 w-5" />
                <span>{courseOverview.totalAssessments} assessments</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <Keyboard className="h-5 w-5" />
                <span>{courseOverview.typingTests} typing tests</span>
              </div>
            </div>
            
            {enrolled ? (
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-white/90"
                onClick={() => navigate('/rpat')}
                data-testid="go-to-dashboard"
              >
                <Play className="mr-2 h-5 w-5" />
                Go to My Dashboard
              </Button>
            ) : (
              <Button 
                size="lg" 
                className="bg-white text-blue-600 hover:bg-white/90"
                onClick={handleEnroll}
                disabled={enrolling}
                data-testid="enroll-btn"
              >
                {enrolling ? (
                  <>Enrolling...</>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-5 w-5" />
                    Enroll Now - Start Your Journey
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Duration</p>
                  <p className="text-2xl font-bold text-cyan-700">{courseOverview.totalHours}h</p>
                </div>
                <Clock className="h-8 w-8 text-cyan-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-violet-50 to-purple-50 border-violet-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Training Phases</p>
                  <p className="text-2xl font-bold text-violet-700">4</p>
                </div>
                <Target className="h-8 w-8 text-violet-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Playbook Pages</p>
                  <p className="text-2xl font-bold text-amber-700">1,200+</p>
                </div>
                <FileText className="h-8 w-8 text-amber-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pass Rate</p>
                  <p className="text-2xl font-bold text-emerald-700">80%+</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Learning Phases */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Training Phases</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {courseOverview.phases.map((phase, index) => (
              <Card key={phase.name} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg ${
                      index === 0 ? 'bg-gradient-to-br from-cyan-500 to-blue-600' :
                      index === 1 ? 'bg-gradient-to-br from-violet-500 to-purple-600' :
                      index === 2 ? 'bg-gradient-to-br from-amber-500 to-orange-600' :
                      'bg-gradient-to-br from-emerald-500 to-green-600'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{phase.name}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{phase.description}</p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-3 w-3" />
                          {phase.topics} topics
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {phase.duration}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Features */}
        <div>
          <h2 className="text-2xl font-bold mb-4">What You&apos;ll Get</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courseOverview.features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={`feature-${feature.text?.substring(0, 20) || index}`} className="bg-gradient-to-br from-slate-50 to-gray-50">
                  <CardContent className="pt-4 flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600">
                      <Icon className="h-5 w-5 text-white" />
                    </div>
                    <p className="text-sm font-medium">{feature.text}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Certification */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600">
                <Award className="h-6 w-6 text-white" />
              </div>
              <div>
                <CardTitle>{courseOverview.certification.name}</CardTitle>
                <CardDescription>Earn your professional certification</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <h4 className="font-medium mb-3">Certification Requirements:</h4>
            <ul className="space-y-2">
              {courseOverview.certification.requirements.map((req, index) => (
                <li key={`cert-req-${req?.substring(0, 20) || index}`} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-amber-600" />
                  {req}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* CTA */}
        {!enrolled && (
          <div className="text-center py-8">
            <h2 className="text-2xl font-bold mb-2">Ready to Begin?</h2>
            <p className="text-muted-foreground mb-6">
              Join thousands of successful recruiters who have completed the R-PAT training program.
            </p>
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
              onClick={handleEnroll}
              disabled={enrolling}
              data-testid="enroll-bottom-btn"
            >
              {enrolling ? 'Enrolling...' : 'Enroll Now'}
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Note */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="pt-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This training program uses voice-based lessons. Please ensure you have a working microphone and speakers. All training content is protected and cannot be copied or downloaded.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};

export default RPATOverview;
